import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications } from '../contexts/NotificationContext';
import { productsAPI, ordersAPI, branchesAPI, departmentAPI } from '../services/api';
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Package,
  AlertCircle,
  Search,
  X,
  ChevronDown,
  ImageIcon,
} from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

// =============== Types ===============
interface Product {
  _id: string;
  name: string;
  nameEn?: string;
  code: string;
  price: number;
  unit?: string;
  unitEn?: string;
  image?: string;
  department: { _id: string; name: string; nameEn?: string };
  displayName: string;
  displayUnit: string;
}

interface OrderItem {
  productId: string;
  product: Product;
  quantity: number;
  price: number;
}

interface Branch {
  _id: string;
  name: string;
  nameEn?: string;
  displayName: string;
}

interface Department {
  _id: string;
  name: string;
  nameEn?: string;
  displayName: string;
}

// =============== Translations ===============
const translations = {
  ar: {
    createOrder: 'إنشاء طلب جديد',
    addProducts: 'قم بإضافة المنتجات وتأكيد الطلب لإرساله',
    noProducts: 'لا توجد منتجات',
    noMatch: 'لا توجد منتجات مطابقة',
    empty: 'لا توجد منتجات متاحة',
    searchPlaceholder: 'ابحث عن المنتجات...',
    code: 'الكود',
    department: 'القسم',
    price: 'السعر',
    unit: 'الوحدة',
    addToCart: 'إضافة إلى السلة',
    branch: 'الفرع',
    branchPlaceholder: 'اختر الفرع',
    branchRequired: 'الفرع مطلوب',
    clearOrder: 'مسح الطلب',
    submitOrder: 'إرسال الطلب',
    submitting: 'جاري الإرسال...',
    orderSummary: 'ملخص الطلب',
    finalTotal: 'الإجمالي النهائي',
    confirmOrder: 'تأكيد الطلب',
    confirmMessage: 'هل أنت متأكد من إرسال الطلب؟',
    cancel: 'إلغاء',
    confirm: 'تأكيد',
    unauthorized: 'غير مصرح لك بالوصول',
    fetchError: 'خطأ أثناء جلب البيانات',
    createError: 'خطأ في إنشاء الطلب',
    cartEmpty: 'السلة فارغة',
    orderCreated: 'تم إنشاء الطلب بنجاح',
    orderCleared: 'تم مسح الطلب',
    scrollToSummary: 'التمرير للملخص',
    currency: 'ريال',
    invalidQuantity: 'الكمية غير صحيحة. للكيلو: 0.25، 0.5، 0.75، 1.0، 1.25، 1.4، 1.7... | لغير الكيلو: عدد صحيح',
  },
  en: {
    createOrder: 'Create New Order',
    addProducts: 'Add products and confirm to submit your order',
    noProducts: 'No Products Found',
    noMatch: 'No Matching Products',
    empty: 'No Products Available',
    searchPlaceholder: 'Search products...',
    code: 'Code',
    department: 'Department',
    price: 'Price',
    unit: 'Unit',
    addToCart: 'Add to Cart',
    branch: 'Branch',
    branchPlaceholder: 'Select Branch',
    branchRequired: 'Branch is required',
    clearOrder: 'Clear Order',
    submitOrder: 'Submit Order',
    submitting: 'Submitting...',
    orderSummary: 'Order Summary',
    finalTotal: 'Final Total',
    confirmOrder: 'Confirm Order',
    confirmMessage: 'Are you sure you want to submit the order?',
    cancel: 'Cancel',
    confirm: 'Confirm',
    unauthorized: 'You are not authorized to access',
    fetchError: 'Error fetching data',
    createError: 'Error creating order',
    cartEmpty: 'Cart is empty',
    orderCreated: 'Order created successfully',
    orderCleared: 'Order cleared',
    scrollToSummary: 'Scroll to Summary',
    currency: 'SAR',
    invalidQuantity: 'Invalid quantity. For kg: 0.25, 0.5, 0.75, 1.0, 1.25, 1.4, 1.7... | For others: integer only',
  },
};

const kgUnits = ['كيلو', 'Kilo', 'كجم', 'kg'];

// =============== Validation ===============
const isValidKgQuantity = (qty: number): boolean => {
  if (qty <= 0) return false;
  const multiplied = Math.round(qty * 100);
  return multiplied % 25 === 0;
};

const isValidQuantity = (quantity: number, unit: string): boolean => {
  if (kgUnits.includes(unit)) return isValidKgQuantity(quantity);
  return Number.isInteger(quantity) && quantity >= 1;
};

// =============== Quantity Input ===============
const QuantityInput = ({
  value,
  onChange,
  onIncrement,
  onDecrement,
  unit,
}: {
  value: number;
  onChange: (val: number) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  unit: string;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [inputValue, setInputValue] = useState<string>(value > 0 ? value.toString() : '');
  const t = translations[isRtl ? 'ar' : 'en'];

  const validateAndSet = (val: string) => {
    const cleanVal = val.replace(',', '.').trim();
    const num = parseFloat(cleanVal);
    if (val === '' || isNaN(num)) {
      setInputValue('');
      onChange(0);
      return;
    }
    if (num <= 0) {
      toast.error(isRtl ? 'الكمية يجب أن تكون أكبر من 0' : 'Quantity must be > 0');
      setInputValue(value > 0 ? value.toString() : '');
      return;
    }
    if (kgUnits.includes(unit) && !isValidKgQuantity(num)) {
      toast.error(t.invalidQuantity);
      setInputValue(value > 0 ? value.toString() : '');
      return;
    }
    if (!kgUnits.includes(unit) && !Number.isInteger(num)) {
      toast.error(isRtl ? 'الكمية يجب أن تكون عددًا صحيحًا' : 'Quantity must be an integer');
      setInputValue(value > 0 ? value.toString() : '');
      return;
    }
    setInputValue(num.toString());
    onChange(num);
  };

  useEffect(() => setInputValue(value > 0 ? value.toString() : ''), [value]);

  return (
    <div className="flex items-center gap-1.5">
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={onDecrement}
        disabled={value <= (kgUnits.includes(unit) ? 0.25 : 1)}
        className="w-7 h-7 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 rounded-full flex items-center justify-center"
      >
        <Minus className="w-3.5 h-3.5" />
      </motion.button>
      <input
        type="text"
        inputMode="decimal"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={() => validateAndSet(inputValue)}
        onKeyDown={(e) => e.key === 'Enter' && validateAndSet(inputValue)}
        className="w-16 h-7 text-center border border-gray-300 rounded text-xs focus:ring-1 focus:ring-amber-500"
        placeholder={(kgUnits.includes(unit) ? '0.25' : '1')}
      />
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={onIncrement}
        className="w-7 h-7 bg-amber-600 hover:bg-amber-700 rounded-full flex items-center justify-center text-white"
      >
        <Plus className="w-3.5 h-3.5" />
      </motion.button>
    </div>
  );
};

// =============== Product Search Input ===============
export const ProductSearchInput = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  ariaLabel: string;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full ${isRtl ? 'pl-10 pr-3' : 'pr-10 pl-3'} py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-sm ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      />
      {value ? (
        <button
          onClick={() => onChange({ target: { value: '' } } as any)}
          className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-amber-600`}
          aria-label={isRtl ? 'مسح' : 'Clear'}
        >
          <X className="w-4 h-4" />
        </button>
      ) : (
        <Search className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4`} />
      )}
    </div>
  );
};

// =============== Product Dropdown ===============
export const ProductDropdown = ({
  value,
  onChange,
  options,
  ariaLabel,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  disabled?: boolean;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white text-sm flex justify-between items-center ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${isRtl ? 'text-right' : 'text-left'}`}
        aria-label={ariaLabel}
      >
        <span className="truncate">{selected.label}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && !disabled && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          className="absolute w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-60 overflow-y-auto"
          onClick={() => setIsOpen(false)}
        >
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => onChange(option.value)}
              className="px-3 py-2.5 text-sm hover:bg-amber-50 cursor-pointer"
            >
              {option.label}
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// =============== Product Card ===============
const ProductCard = ({
  product,
  cartItem,
  onAdd,
  onUpdate,
  isRtl,
  t,
}: {
  product: Product;
  cartItem?: OrderItem;
  onAdd: () => void;
  onUpdate: (quantity: number) => void;
  isRtl: boolean;
  t: typeof translations['ar'];
}) => {
  const isKg = kgUnits.includes(product.displayUnit);
  const step = isKg ? 0.25 : 1;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col"
    >
      <div className="h-32 bg-gray-50 relative">
        {product.image ? (
          <img
            src={product.image}
            alt={product.displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div className={`absolute inset-0 flex items-center justify-center ${product.image ? 'hidden' : ''}`}>
          <ImageIcon className="w-6 h-6 text-gray-300" />
        </div>
      </div>
      <div className="p-3 space-y-2 flex flex-col flex-grow">
        <div className="flex justify-between gap-1">
          <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 min-w-0">
            {product.displayName}
          </h3>
          <span className="text-xs text-gray-500 flex-shrink-0 whitespace-nowrap">
            {product.code}
          </span>
        </div>
        <p className="text-xs text-amber-600 line-clamp-1">
          {isRtl ? product.department.name : product.department.nameEn || product.department.name}
        </p>
        <p className="text-sm font-medium text-gray-900 mt-auto">
          {product.price} {isRtl ? 'ريال' : 'SAR'} / {product.displayUnit}
        </p>
        <div className="mt-2 flex justify-end">
          {cartItem ? (
            <QuantityInput
              value={cartItem.quantity}
              onChange={onUpdate}
              onIncrement={() => onUpdate(cartItem.quantity + step)}
              onDecrement={() => onUpdate(Math.max(step, cartItem.quantity - step))}
              unit={product.displayUnit}
            />
          ) : (
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onAdd}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              {t.addToCart}
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============== Order Summary Item ===============
const OrderSummaryItem = ({
  item,
  onUpdate,
  onRemove,
  isRtl,
  t,
}: {
  item: OrderItem;
  onUpdate: (q: number) => void;
  onRemove: () => void;
  isRtl: boolean;
  t: typeof translations['ar'];
}) => {
  return (
    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 relative">
      {/* زر الحذف العائم في الزاوية العلوية اليسرى */}
      <motion.button
        whileTap={{ scale: 0.9 }}
        onClick={onRemove}
        className="absolute -top-2 -left-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm z-10"
        aria-label={isRtl ? 'إزالة' : 'Remove'}
      >
        <X className="w-2.5 h-2.5" />
      </motion.button>

      <div className="flex items-center justify-between gap-3 pt-1">
        {/* الجانب الأيسر: الصورة + النصوص */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded bg-white border border-gray-200 flex-shrink-0 flex items-center justify-center">
            {item.product.image ? (
              <img
                src={item.product.image}
                alt={item.product.displayName}
                className="w-10 h-10 object-cover rounded"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : (
              <ImageIcon className="w-10 h-10 text-gray-400" />
            )}
          </div>

          <div className="min-w-0">
            <p className="font-medium text-xs text-gray-900 line-clamp-1">
              {item.product.displayName}
            </p>
            <p className="text-xs text-gray-600 mt-0.5">
              {item.price} {t.currency} / {item.product.displayUnit}
            </p>
          </div>
        </div>

        {/* الجانب الأيمن: الكمية فقط (بدون زر حذف هنا) */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() =>
              onUpdate(
                Math.max(
                  kgUnits.includes(item.product.displayUnit) ? 0.25 : 1,
                  item.quantity - (kgUnits.includes(item.product.displayUnit) ? 0.25 : 1)
                )
              )
            }
            disabled={item.quantity <= (kgUnits.includes(item.product.displayUnit) ? 0.25 : 1)}
            className="w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 disabled:opacity-50"
          >
            <Minus className="w-2 h-2 text-gray-700" />
          </motion.button>

          <input
            type="text"
            value={item.quantity}
            onChange={(e) => {
              const val = parseFloat(e.target.value.replace(',', '.'));
              if (!isNaN(val)) onUpdate(val);
            }}
            className="w-10 h-5 text-center text-xs border border-gray-300 rounded focus:ring-1 focus:ring-amber-500 focus:outline-none"
          />

          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() =>
              onUpdate(item.quantity + (kgUnits.includes(item.product.displayUnit) ? 0.25 : 1))
            }
            className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-600"
          >
            <Plus className="w-2 h-2 text-white" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};




// =============== Skeleton ===============
const ProductSkeletonCard = () => (
  <div className="p-3 bg-white rounded-xl border border-gray-200 animate-pulse h-full flex flex-col">
    <div className="h-32 bg-gray-200 rounded mb-2"></div>
    <div className="space-y-2 flex-grow">
      <div className="h-3 bg-gray-200 rounded w-3/4"></div>
      <div className="h-2 bg-gray-200 rounded w-1/2"></div>
      <div className="h-2 bg-gray-200 rounded w-1/3"></div>
      <div className="mt-auto h-7 bg-gray-200 rounded w-20"></div>
    </div>
  </div>
);

// =============== Confirm Modal ===============
const OrderConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  submitting,
  t,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  submitting: boolean;
  t: typeof translations['ar'];
}) => {
  if (!isOpen) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-bold mb-2">{t.confirmOrder}</h3>
          <p className="text-sm text-gray-600 mb-4">{t.confirmMessage}</p>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 bg-gray-200 rounded text-sm">
              {t.cancel}
            </button>
            <button
              onClick={onConfirm}
              disabled={submitting}
              className="px-3 py-1.5 bg-amber-600 text-white rounded text-sm disabled:opacity-60"
            >
              {submitting ? t.submitting : t.confirm}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// =============== Main Component ===============
export function NewOrder() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const { addNotification } = useNotifications();
  const isRtl = language === 'ar';
  const t = translations[isRtl ? 'ar' : 'en'];
  const summaryRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(true);

  const [products, setProducts] = useState<Product[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [branch, setBranch] = useState<string>(user?.branchId?.toString() || '');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const socket = useMemo<Socket>(() => io('https://aljodia.com'), []);
  const debouncedSearch = useCallback(debounce((v: string) => setSearchTerm(v.trim()), 500), []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSearchInput(v);
    if (v.length >= 2 || v === '') debouncedSearch(v);
  };

  const filteredProducts = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    return products.filter(
      (p) =>
        (!filterDepartment || p.department._id === filterDepartment) &&
        (p.displayName.toLowerCase().includes(lower) || p.code.toLowerCase().includes(lower))
    );
  }, [products, searchTerm, filterDepartment]);

  useEffect(() => {
    if (!user || !['admin', 'branch'].includes(user.role)) {
      setError(t.unauthorized);
      toast.error(t.unauthorized);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const [prodRes, branchRes, deptRes] = await Promise.all([
          productsAPI.getAll({ department: filterDepartment, search: searchTerm, limit: 0 }),
          branchesAPI.getAll(),
          departmentAPI.getAll({ limit: 100 }),
        ]);

        const prods = (prodRes.data || []).map((p: any) => ({
          ...p,
          displayName: isRtl ? p.name : p.nameEn || p.name,
          displayUnit: isRtl ? p.unit || 'غير محدد' : p.unitEn || p.unit || 'N/A',
          department: {
            ...p.department,
            displayName: isRtl ? p.department.name : p.department.nameEn || p.department.name,
          },
          image: p.image || '',
        }));
        setProducts(prods);

        setBranches(
          (branchRes.data || []).map((b: any) => ({
            ...b,
            displayName: isRtl ? b.name : b.nameEn || b.name,
          }))
        );

        setDepartments(
          (deptRes.data || []).map((d: any) => ({
            ...d,
            displayName: isRtl ? d.name : d.nameEn || d.name,
          }))
        );

        if (user.role === 'branch' && user.branchId) setBranch(user.branchId.toString());
      } catch (err: any) {
        setError(err.message || t.fetchError);
        toast.error(err.message || t.fetchError);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, isRtl, filterDepartment, searchTerm, t]);

  useEffect(() => {
    setProducts((prev) =>
      prev.map((p) => ({
        ...p,
        displayName: isRtl ? p.name : p.nameEn || p.name,
        displayUnit: isRtl ? p.unit || 'غير محدد' : p.unitEn || p.unit || 'N/A',
        department: {
          ...p.department,
          displayName: isRtl ? p.department.name : p.department.nameEn || p.department.name,
        },
      }))
    );
    setOrderItems((prev) =>
      prev.map((item) => ({
        ...item,
        product: {
          ...item.product,
          displayName: isRtl ? item.product.name : item.product.nameEn || item.product.name,
          displayUnit: isRtl
            ? item.product.unit || 'غير محدد'
            : item.product.unitEn || item.product.unit || 'N/A',
          department: {
            ...item.product.department,
            displayName: isRtl
              ? item.product.department.name
              : item.product.department.nameEn || item.product.department.name,
          },
        },
      }))
    );
  }, [isRtl]);

  useEffect(() => {
    if (!user || !socket) return;
    socket.on('connect', () => {
      if (user.role === 'branch' && user.branchId) {
        socket.emit('joinRoom', `branch-${user.branchId}`);
      } else if (['admin', 'production'].includes(user.role)) {
        socket.emit('joinRoom', user.role);
      }
    });
    socket.on('orderCreated', (data) => {
      if (!data?._id) return;
    });
    return () => socket.disconnect();
  }, [socket, user]);

  // 👇 Scroll button visibility logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth >= 1024) return; // Desktop: no scroll button
      const isNearBottom = window.scrollY + window.innerHeight >= document.body.scrollHeight - 100;
      setShowScrollButton(!isNearBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const addToOrder = useCallback((product: Product) => {
    const isKg = kgUnits.includes(product.displayUnit);
    const step = isKg ? 0.25 : 1;
    setOrderItems((prev) => {
      const existing = prev.find((i) => i.productId === product._id);
      const qty = existing ? existing.quantity + step : step;
      if (!isValidQuantity(qty, product.displayUnit)) {
        toast.error(t.invalidQuantity);
        return prev;
      }
      if (existing) return prev.map((i) => (i.productId === product._id ? { ...i, quantity: qty } : i));
      return [...prev, { productId: product._id, product, quantity: step, price: product.price }];
    });
  }, [t]);

  const updateQuantity = useCallback(
    (id: string, qty: number) => {
      const item = orderItems.find((i) => i.productId === id);
      if (!item) return;
      const min = kgUnits.includes(item.product.displayUnit) ? 0.25 : 1;
      if (qty < min) {
        setOrderItems((prev) => prev.filter((i) => i.productId !== id));
        return;
      }
      if (!isValidQuantity(qty, item.product.displayUnit)) {
        toast.error(t.invalidQuantity);
        return;
      }
      setOrderItems((prev) => prev.map((i) => (i.productId === id ? { ...i, quantity: qty } : i)));
    },
    [orderItems, t]
  );

  const removeFromOrder = useCallback((id: string) => setOrderItems((prev) => prev.filter((i) => i.productId !== id)), []);

  const clearOrder = useCallback(() => {
    setOrderItems([]);
    if (user?.role === 'admin') setBranch('');
    setSearchInput('');
    setSearchTerm('');
    setFilterDepartment('');
  }, [user]);

  const total = useMemo(() => orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0).toFixed(2), [orderItems]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (orderItems.length === 0) return toast.error(t.cartEmpty);
    if (user?.role === 'admin' && !branch) return toast.error(t.branchRequired);
    setShowConfirmModal(true);
  };

  const confirmOrder = async () => {
    setSubmitting(true);
    try {
      const eventId = crypto.randomUUID();
      const branchId = user?.role === 'branch' ? user.branchId : branch;
      const orderData = {
        orderNumber: `ORD-${Date.now()}`,
        branchId,
        items: orderItems.map((i) => ({
          product: i.productId,
          quantity: i.quantity,
          price: i.price,
          productName: i.product.name,
          productNameEn: i.product.nameEn,
          unit: i.product.unit || 'غير محدد',
          unitEn: i.product.unitEn || 'N/A',
          department: i.product.department,
        })),
        status: 'pending',
        priority: 'medium',
        createdBy: user?.id || user?._id,
        isRtl,
        eventId,
      };

      const res = await ordersAPI.create(orderData, isRtl);
      socket.emit('orderCreated', {
        _id: res.data.id,
        orderNumber: res.data.orderNumber,
        eventId,
        isRtl,
      });

     

      clearOrder();
      setShowConfirmModal(false);
    } catch (err: any) {
      toast.error(err.message || t.createError);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSummary = () => summaryRef.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className={`mx-auto px-4 py-6 min-h-screen ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="mb-6 flex flex-col items-center md:items-start text-center md:text-left">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-6 h-6 text-amber-600" />
          <h1 className="text-xl font-bold">{t.createOrder}</h1>
        </div>
        <p className="text-gray-600 text-sm mt-1">{t.addProducts}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-red-600 text-sm">{error}</span>
        </div>
      )}

      {orderItems.length > 0 && showScrollButton && (
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={scrollToSummary}
          className="fixed bottom-6 left-3 p-3 bg-amber-600 text-white rounded-full shadow-lg z-40 lg:hidden"
          aria-label={t.scrollToSummary}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.button>
      )}

      <div className={`space-y-6 ${orderItems.length > 0 ? 'lg:grid lg:grid-cols-[2fr_1fr] lg:gap-6 lg:items-start' : ''}`}>
        <div>
          <div className="p-4 bg-white rounded-xl border">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <ProductSearchInput
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder={t.searchPlaceholder}
                  ariaLabel={t.searchPlaceholder}
                />
              </div>
              <div className="sm:col-span-1">
                <ProductDropdown
                  value={filterDepartment}
                  onChange={setFilterDepartment}
                  options={[
                    { value: '', label: isRtl ? 'كل الأقسام' : 'All Departments' },
                    ...departments.map((d) => ({ value: d._id, label: d.displayName })),
                  ]}
                  ariaLabel={t.department}
                />
              </div>
            </div>
            <p className="mt-2 text-center text-sm text-gray-600">
              {filteredProducts.length} {isRtl ? 'منتج' : 'Products'}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              {[...Array(6)].map((_, i) => (
                <ProductSkeletonCard key={i} />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-6 text-center bg-white rounded-xl border mt-4">
              <Package className="w-10 h-10 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">{searchTerm || filterDepartment ? t.noMatch : t.empty}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              <AnimatePresence>
                {filteredProducts.map((p) => {
                  const item = orderItems.find((i) => i.productId === p._id);
                  return (
                    <ProductCard
                      key={p._id}
                      product={p}
                      cartItem={item}
                      onAdd={() => addToOrder(p)}
                      onUpdate={(q) => updateQuantity(p._id, q)}
                      isRtl={isRtl}
                      t={t}
                    />
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>

        {orderItems.length > 0 && (
          <div
            className="space-y-4 lg:sticky lg:top-6 max-h-[calc(100vh-4rem)] overflow-y-auto"
            ref={summaryRef}
          >
            <div className="p-4 bg-white rounded-xl border">
              <h3 className="text-lg font-bold mb-3">{t.orderSummary}</h3>
              <div className="space-y-3">
                {orderItems.map((item) => (
                  <OrderSummaryItem
                    key={item.productId}
                    item={item}
                    onUpdate={(q) => updateQuantity(item.productId, q)}
                    onRemove={() => removeFromOrder(item.productId)}
                    isRtl={isRtl}
                    t={t}
                  />
                ))}
              </div>
              <div className="border-t pt-3 flex justify-between font-bold text-sm mt-4">
                <span>{t.finalTotal}:</span>
                <span className="text-amber-600">{total} {t.currency}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 bg-white rounded-xl border space-y-3">
              {user?.role === 'admin' && (
                <ProductDropdown
                  value={branch}
                  onChange={setBranch}
                  options={[
                    { value: '', label: t.branchPlaceholder },
                    ...branches.map((b) => ({ value: b._id, label: b.displayName })),
                  ]}
                  ariaLabel={t.branch}
                />
              )}
              <div className="flex gap-2">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={clearOrder}
                  className="flex-1 py-2.5 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                  disabled={submitting}
                >
                  {t.clearOrder}
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm"
                  disabled={submitting}
                >
                  {submitting ? t.submitting : t.submitOrder}
                </motion.button>
              </div>
            </form>
          </div>
        )}
      </div>

      <OrderConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmOrder}
        submitting={submitting}
        t={t}
      />
    </div>
  );
}